/**
 * Firm-communication threads on the case-detail panel (#542, slice 1).
 *
 * The matter's Portal Messages threads, read through the CC worker's proxy
 * (`use-firm-messages`). The row + expanded-thread + reply rendering lives in
 * the shared `FirmThreadList` (slice 2 extracted it so the global Messages
 * view and this section stay one surface vocabulary); this component owns the
 * per-matter fetch and its loading/error/empty states. No case-label chip
 * here — the matter is already the panel's context.
 *
 * Distinct from a pin's operator Notes by structure, not paint — these are
 * firm-facing conversations with subjects and sender identity; Notes are
 * LF-internal coordination on a task (#542 decision 7).
 */

import { useCaseThreads } from "@/lib/use-firm-messages";
import { FirmThreadList } from "@/components/firm-thread-list";

export function CaseMessages({
  leadId,
  testId = "case-messages",
}: {
  leadId: string;
  testId?: string;
}) {
  const threads = useCaseThreads(leadId, true);

  if (threads.isLoading) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testId}-loading`}>
        Loading messages…
      </p>
    );
  }
  if (threads.isError) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testId}-error`}>
        Couldn't load messages. Close and reopen the case to retry.
      </p>
    );
  }

  const list = threads.data?.threads ?? [];
  if (list.length === 0) {
    return (
      <p className="text-sm italic text-muted-foreground" data-testid={`${testId}-empty`}>
        No firm messages on this matter.
      </p>
    );
  }

  return <FirmThreadList threads={list} testId={testId} />;
}
